<template>
  <div class="form-page">
    <div class="form-page__header">
      <h2 class="form-page__title">Antragstellung als rechtliche Vertretung</h2>
      <p class="form-page__subtitle">Geben Sie an, ob Sie den Antrag für sich selbst oder als rechtliche Vertretung einer anderen Person stellen.</p>
    </div>

    <div class="form-section">
      <h3 class="section-title">Art der Antragstellung <span class="required-mark">*</span></h3>
      <div class="radio-group" :class="{ 'field--error': showErrors && !modelValue.antragstellungsart }">
        <label class="radio-label">
          <input
            type="radio"
            name="antragstellungsart"
            value="eigenantrag"
            :checked="modelValue.antragstellungsart === 'eigenantrag'"
            @change="update('antragstellungsart', 'eigenantrag')"
            class="radio-input"
          />
          <span class="radio-custom"></span>
          <span class="radio-text">Ich stelle den Antrag für mich selbst (Eigenantrag)</span>
        </label>
        <label class="radio-label">
          <input
            type="radio"
            name="antragstellungsart"
            value="vertretung"
            :checked="modelValue.antragstellungsart === 'vertretung'"
            @change="update('antragstellungsart', 'vertretung')"
            class="radio-input"
          />
          <span class="radio-custom"></span>
          <span class="radio-text">Ich stelle den Antrag als rechtliche Vertretung (z. B. Elternteil, Vormund)</span>
        </label>
        <p v-if="showErrors && !modelValue.antragstellungsart" class="field__error">Bitte wählen Sie eine Option aus.</p>
      </div>
    </div>

    <!-- Personal data section — AutoFill target -->
    <div class="form-section">
      <h3 class="section-title">
        Persönliche Angaben
        <span v-if="hasAutofill" class="badge badge--blue" style="margin-left: 10px; vertical-align: middle;">
          ✦ AutoFill
        </span>
      </h3>
      <div class="field-group">
        <div class="field-row field-row--2">
          <div class="field">
            <label class="field__label">Anrede <span class="required-mark">*</span></label>
            <select
              class="field__select"
              :class="[{ 'field--error': showErrors && !modelValue.anrede }, { 'autofill-highlight': modelValue._autofilled?.anrede }]"
              :value="modelValue.anrede"
              @change="update('anrede', $event.target.value)"
            >
              <option value="">– Bitte wählen –</option>
              <option value="Frau">Frau</option>
              <option value="Herr">Herr</option>
              <option value="Divers">Divers / Keine Angabe</option>
            </select>
            <p v-if="showErrors && !modelValue.anrede" class="field__error">Pflichtfeld</p>
          </div>

          <div class="field">
            <label class="field__label">Titel (optional)</label>
            <input
              type="text"
              class="field__input"
              :class="{ 'autofill-highlight': modelValue._autofilled?.titel }"
              placeholder="z. B. Dr."
              :value="modelValue.titel"
              @input="update('titel', $event.target.value)"
            />
          </div>
        </div>

        <div class="field-row field-row--2">
          <div class="field">
            <label class="field__label">Vorname <span class="required-mark">*</span></label>
            <input
              type="text"
              class="field__input"
              :class="[{ 'field--error': showErrors && !modelValue.vorname }, { 'autofill-highlight': modelValue._autofilled?.vorname }]"
              placeholder="Vorname"
              :value="modelValue.vorname"
              @input="update('vorname', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.vorname" class="field__error">Pflichtfeld</p>
          </div>

          <div class="field">
            <label class="field__label">Nachname <span class="required-mark">*</span></label>
            <input
              type="text"
              class="field__input"
              :class="[{ 'field--error': showErrors && !modelValue.nachname }, { 'autofill-highlight': modelValue._autofilled?.nachname }]"
              placeholder="Nachname"
              :value="modelValue.nachname"
              @input="update('nachname', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.nachname" class="field__error">Pflichtfeld</p>
          </div>
        </div>

        <div class="field-row field-row--2">
          <div class="field">
            <label class="field__label">Geburtsdatum <span class="required-mark">*</span></label>
            <input
              type="date"
              class="field__input"
              :class="[{ 'field--error': showErrors && !modelValue.geburtsdatum }, { 'autofill-highlight': modelValue._autofilled?.geburtsdatum }]"
              :value="modelValue.geburtsdatum"
              @input="update('geburtsdatum', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.geburtsdatum" class="field__error">Pflichtfeld</p>
          </div>

          <div class="field">
            <label class="field__label">Geburtsort <span class="required-mark">*</span></label>
            <input
              type="text"
              class="field__input"
              :class="[{ 'field--error': showErrors && !modelValue.geburtsort }, { 'autofill-highlight': modelValue._autofilled?.geburtsort }]"
              placeholder="Geburtsort"
              :value="modelValue.geburtsort"
              @input="update('geburtsort', $event.target.value)"
            />
            <p v-if="showErrors && !modelValue.geburtsort" class="field__error">Pflichtfeld</p>
          </div>
        </div>

        <div class="field">
          <label class="field__label">Staatsangehörigkeit (aktuell) <span class="required-mark">*</span></label>
          <select
            class="field__select"
            :class="[{ 'field--error': showErrors && !modelValue.staatsangehoerigkeit }, { 'autofill-highlight': modelValue._autofilled?.staatsangehoerigkeit }]"
            :value="modelValue.staatsangehoerigkeit"
            @change="update('staatsangehoerigkeit', $event.target.value)"
          >
            <option value="">– Bitte wählen –</option>
            <option value="türkisch">Türkisch</option>
            <option value="russisch">Russisch</option>
            <option value="polnisch">Polnisch</option>
            <option value="italienisch">Italienisch</option>
            <option value="griechisch">Griechisch</option>
            <option value="rumänisch">Rumänisch</option>
            <option value="bulgarisch">Bulgarisch</option>
            <option value="kroatisch">Kroatisch</option>
            <option value="serbisch">Serbisch</option>
            <option value="amerikanisch">Amerikanisch (USA)</option>
            <option value="britisch">Britisch</option>
            <option value="französisch">Französisch</option>
            <option value="spanisch">Spanisch</option>
            <option value="sonstige">Sonstige</option>
          </select>
          <p v-if="showErrors && !modelValue.staatsangehoerigkeit" class="field__error">Pflichtfeld</p>
        </div>

        <div class="field">
          <label class="field__label">E-Mail-Adresse <span class="required-mark">*</span></label>
          <input
            type="email"
            class="field__input"
            :class="[{ 'field--error': showErrors && !modelValue.email }, { 'autofill-highlight': modelValue._autofilled?.email }]"
            placeholder="beispiel@email.de"
            :value="modelValue.email"
            @input="update('email', $event.target.value)"
          />
          <p v-if="showErrors && !modelValue.email" class="field__error">Pflichtfeld</p>
        </div>

        <div class="field">
          <label class="field__label">Mobiltelefon <span class="required-mark">*</span></label>
          <input
            type="tel"
            class="field__input"
            :class="[{ 'field--error': showErrors && !modelValue.mobil }, { 'autofill-highlight': modelValue._autofilled?.mobil }]"
            placeholder="+49 170 1234567"
            :value="modelValue.mobil"
            @input="update('mobil', $event.target.value)"
          />
          <p v-if="showErrors && !modelValue.mobil" class="field__error">Pflichtfeld</p>
        </div>
      </div>
    </div>

    <p class="required-note"><span class="required-mark">*</span> Pflichtfelder</p>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'NatStep2',
  props: {
    modelValue: { type: Object, required: true },
    showErrors: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const update = (key, val) => emit('update:modelValue', { ...props.modelValue, [key]: val });
    const hasAutofill = computed(() => props.modelValue._autofilled && Object.keys(props.modelValue._autofilled).length > 0);
    return { update, hasAutofill };
  },
};
</script>

<style scoped src="../form.css"></style>
